import React, { useState, useEffect, useMemo } from "react";
import { Input, Drawer } from "antd";
import {
  CloseOutlined,
  SearchOutlined,
  RightOutlined,
  GiftOutlined,
  CreditCardOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import db from "../../../../database";
import { getPricingRules } from "../../../../utils/pricingRulesCache";
import { describeOfferScope, describeSchedule, loadOfferScopeNames } from "../../../../utils/fashion/offerScope";
import { DRAWER_MASK } from "../../Core/modals/customerDrawerUi";
import useCartStore from "../../../../stores/useCartStore";
import "./v4Modals.css";

const TAG_ICONS = {
  ITEM: <GiftOutlined />,
  BILL: <ShoppingCartOutlined />,
  BXGY: <TagsOutlined />,
  COMBO: <AppstoreOutlined />,
  DEFAULT: <TagsOutlined />,
  BXPY: <CreditCardOutlined />,
};

const TYPE_TO_TAG = {
  PD: "ITEM",
  FD: "BILL",
  QD: "ITEM",
  TD: "BILL",
  TDF: "BILL",
  EXD: "BILL",
  PA: "ITEM",
  XGY: "BXGY",
  BXD: "BXPY",
  BXS: "BXPY",
  PK: "COMBO",
};

const APPLIES_TO_BY_TYPE = {
  TD: "Entire Bill",
  PD: "Entire Bill",
  TDF: "Entire Bill",
  FD: "Entire Bill",
  PK: "Entire Bill",
  XGY: "Selected Items",
  BXS: "Selected Items",
  BXD: "Selected Items",
  EXD: "Selected Items",
  PA: "Selected Items",
  QD: "Selected Items",
};

const resolveTagMeta = (rule) => {
  const type = rule?.type;
  return {
    label: TYPE_TO_TAG[type] || type || "DEFAULT",
    group: TYPE_TO_TAG[type] || "DEFAULT",
    icon: TAG_ICONS[TYPE_TO_TAG[type]] || TAG_ICONS.DEFAULT,
  };
};

// One tab per kind of offer the cashier recognises (bill offer, buy X get Y…),
// not per rule type: several rule types are the same kind of offer.
const buildCategoriesFromDB = (pricingRules) => {
  const categories = {};
  pricingRules.forEach((rule) => {
    const group = TYPE_TO_TAG[rule.type] || "DEFAULT";
    if (!categories[group]) {
      categories[group] = {
        key: group,
        label: TAG_LABELS[group] || "Other offers",
        count: 0,
        offers: [],
      };
    }
    categories[group].count += 1;
    categories[group].offers.push(rule);
  });
  return {
    all: {
      key: "all",
      label: "ALL",
      count: pricingRules.length,
      offers: pricingRules,
    },
    ...categories,
  };
};

// What each tag group means to a cashier, in words rather than codes.
const TAG_LABELS = {
  ITEM: "Item offer",
  BILL: "Bill offer",
  BXGY: "Buy X get Y",
  BXPY: "Buy X pay Y",
  COMBO: "Combo",
};

// scope / schedule are passed only when the till opts in (showScope, Fashion):
// what the offer applies to from its own lists, and its days and hours.
// Ids of the pricing rules on the current bag: each live line's rule plus the
// bill-level rule. Read-only; this screen never applies or removes an offer.
const appliedRuleIds = (cart) => {
  const ids = new Set();
  (cart?.items || []).forEach((item) => {
    if (item?.isCanceled !== "Y" && item?.mPricingruleId) ids.add(item.mPricingruleId);
  });
  if (cart?.mPricingruleId) ids.add(cart.mPricingruleId);
  return ids;
};

// What each line offer is saving on the current bag, by rule id. Read off the
// lines as priced; nothing is recalculated here.
export const ruleSavings = (cart) => {
  const out = {};
  (cart?.items || []).forEach((item) => {
    const amt = Number(item?.discount);
    if (item?.isCanceled === "Y" || !item?.mPricingruleId || !(amt > 0)) return;
    out[item.mPricingruleId] = (out[item.mPricingruleId] || 0) + amt;
  });
  return out;
};

const OfferCard = ({ title, description, ruleType, valid, tagMeta, onViewDetails, scope, schedule, applied = false, saving }) => {
  const appliesTo = scope || APPLIES_TO_BY_TYPE[ruleType] || "Entire Bill";

  return (
    <article className={`v4m-offer${applied ? " applied" : ""}`}>
      <div className="v4m-offer-top">
        <b>{title}</b>
        <span className="v4m-chips" style={{ flex: "none", justifyContent: "flex-end" }}>
          {saving && <span className="v4m-chip v4m-saving">{saving}</span>}
          {applied && <span className="v4m-chip v4m-applied">Applied</span>}
          <span className="v4m-chip" style={{ gap: 4 }}>
            {tagMeta.icon}
            {TAG_LABELS[tagMeta.group] || tagMeta.label}
          </span>
        </span>
      </div>
      {description && <p title={description}>{description}</p>}
      <div className="v4m-meta">
        <span title={scope ? appliesTo : undefined}>
          <b>Applies To:</b> <span>{appliesTo}</span>
        </span>
        {schedule && (
          <span>
            <b>When:</b> <span>{schedule}</span>
          </span>
        )}
        <span>
          <b>Valid:</b> <span>{valid}</span>
        </span>
      </div>
      <button type="button" className="v4m-link" onClick={onViewDetails}>
        View details <RightOutlined style={{ fontSize: 11 }} />
      </button>
    </article>
  );
};

const OffersDrawer = ({
  offersDrawerOpen,
  setOffersDrawerOpen,
  handleViewDetails,
  formatDate,
  showScope = false,
  currSymbol = "",
}) => {
  const [categories, setCategories] = useState({});
  const [scopeNames, setScopeNames] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchText, setSearchText] = useState("");
  const cart = useCartStore((state) => state.cart);
  const appliedIds = useMemo(() => appliedRuleIds(cart), [cart]);
  const savings = useMemo(() => ruleSavings(cart), [cart]);
  const hasBill = (cart?.items || []).some((item) => item?.isCanceled !== "Y");

  const handleCloseDrawer = () => {
    setOffersDrawerOpen(false);
    setSearchText("");
    setActiveCategory("all");
  };

  useEffect(() => {
    if (offersDrawerOpen) {
      setSearchText("");
      setActiveCategory("all");
    }
  }, [offersDrawerOpen]);

  useEffect(() => {
    const pricingRules = getPricingRules();
    const categorized = buildCategoriesFromDB(pricingRules);
    setCategories(categorized);
  }, []);

  // Opt-in (Fashion): look up the names of the products, categories and brands
  // each offer names. Off by default, so other tills read nothing extra.
  useEffect(() => {
    if (!showScope) return undefined;
    let live = true;
    loadOfferScopeNames(categories.all?.offers || []).then((names) => {
      if (live) setScopeNames(names);
    });
    return () => {
      live = false;
    };
  }, [showScope, categories]);

  const offers = useMemo(() => {
    const categoryOffers = activeCategory === "all" ? categories.all?.offers || [] : categories[activeCategory]?.offers || [];
    const search = searchText.trim().toLowerCase();
    const matched = search
      ? categoryOffers.filter((rule) => (rule.printedName || rule.name || "").toLowerCase().includes(search))
      : categoryOffers;
    // Offers already on the bag first; the rest keep their order.
    if (appliedIds.size === 0) return matched;
    return [...matched.filter((rule) => appliedIds.has(rule.mPricingrulesId)), ...matched.filter((rule) => !appliedIds.has(rule.mPricingrulesId))];
  }, [categories, activeCategory, searchText, appliedIds]);
  const appliedCount = (categories.all?.offers || []).filter((rule) => appliedIds.has(rule.mPricingrulesId)).length;

  return (
    <Drawer
      placement="bottom"
      height="93vh"
      open={offersDrawerOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      onClose={handleCloseDrawer}
      className="v4m"
      styles={{ mask: DRAWER_MASK, body: { display: "flex", flexDirection: "column", gap: 12 } }}
    >
      <div className="v4m-head" style={{ marginBottom: 0, alignItems: "center" }}>
        <div>
          <h2 className="v4m-title">{hasBill ? "Offers for this bill" : "Offers & Promotions"}</h2>
          <p className="v4m-sub">
            {appliedCount > 0 ? `${appliedCount} applied to this bill · ` : ""}
            Only one bill-level offer can apply. The best one is picked for you.
          </p>
        </div>
        <Input
          className="v4m-in"
          placeholder="Search offers by name"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 320, flex: "none" }}
        />
        <button type="button" className="v4m-ib" aria-label="Close" onClick={handleCloseDrawer}>
          <CloseOutlined />
        </button>
      </div>

      <div className="v4m-seg category-scroll" role="tablist" aria-label="Offer types">
        {Object.values(categories).map((cat) => {
          const isActive = activeCategory === cat.key;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              key={cat.key}
              className={`v4m-segb${isActive ? " on" : ""}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.key === "all" ? "All" : cat.label}
              <i>{cat.count}</i>
            </button>
          );
        })}
      </div>

      <div className="v4m-offers" style={{ flex: 1, minHeight: 0 }}>
        {offers.length === 0 && (
          <p className="v4m-empty" style={{ gridColumn: "1 / -1" }}>
            {searchText.trim() ? `No offer matches “${searchText.trim()}”.` : "No offers are running at this store right now."}
          </p>
        )}
        {offers.map((rule) => (
          <OfferCard
            ruleType={rule.type}
            key={rule.mPricingrulesId}
            title={rule.printedName || rule.name}
            description={rule.description || ""}
            tagMeta={resolveTagMeta(rule)}
            valid={`${formatDate(rule.startDate)} – ${formatDate(rule.endDate)}`}
            onViewDetails={() => handleViewDetails(rule)}
            scope={showScope ? describeOfferScope(rule, scopeNames || {}) : undefined}
            schedule={showScope ? describeSchedule(rule) : undefined}
            applied={appliedIds.has(rule.mPricingrulesId)}
            saving={savings[rule.mPricingrulesId] ? `−${currSymbol}${savings[rule.mPricingrulesId].toFixed(2)}` : undefined}
          />
        ))}
      </div>
    </Drawer>
  );
};

export default OffersDrawer;
